import { PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Loading } from '@/components/ui/daisy';
import useFormList from '@/data/hook/shared/useFormList';

import { ExamFormFields } from './ExamForm';
import ExamInlineForm from './ExamInlineForm';
import { BaseExamCoursePath } from '@/data/hook/exam/useExams';
import PedWorkspaceGuard from '@/components/shared/PedWorkspaceGuard';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

type ExamFormListProps = {
  coursePath: BaseExamCoursePath;
  courseLineId: number;
  onSubmit: (data: ExamFormFields) => Promise<unknown>;
  isLoading: boolean;
};

export default function ExamFormList({
  coursePath,
  courseLineId,
  onSubmit,
  isLoading,
}: ExamFormListProps) {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examsTab',
  });
  const { openForms, addForm, removeForm, handleFormSubmit } =
    useFormList<ExamFormFields>({ onSubmit });

  return (
    <div className="flex flex-col gap-6">
      <ConditionalRenderer condition={isLoading}>
        <FieldsetContainer className="flex justify-center items-center py-5">
          <Loading variant="spinner" size="md" color="primary" />
        </FieldsetContainer>
      </ConditionalRenderer>
      {openForms.map(formId => (
        <ExamInlineForm
          key={formId}
          courseLineId={courseLineId}
          coursePath={coursePath}
          onSubmit={handleFormSubmit(formId)}
          onCancel={() => removeForm(formId)}
          isLoading={isLoading}
        />
      ))}
      <FieldsetContainer>
        <PedWorkspaceGuard placement="top">
          <Button
            size="md"
            onClick={addForm}
            className="bg-base-300 w-full"
            disabled={isLimitedEdition}
          >
            <PlusIcon className="w-4 h-4" />
            {t('createExam')}
          </Button>
        </PedWorkspaceGuard>
      </FieldsetContainer>
    </div>
  );
}
